/**
 * ============================================================================
 * RiskRegister — Phase 7 frontend (Quality Risk Management Module, ICH Q9).
 * Owner: Phase 7 — risk-svc.
 * Route: /risks
 *
 * Purpose: top-level QA / RiskOwner workbench. A sortable table of all risks
 *   in the tenant, colour-coded by RiskLevel (CRITICAL/HIGH/MEDIUM/LOW), with
 *   per-column sort and category/level filters. The table defaults to sorting
 *   by RPN descending (worst risks first) — RSK-009.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading: skeleton rows.
 *   - Error: refined envelope with Retry button.
 *   - Colour is never the sole signal — every chip carries a text label.
 *
 * URS rows: RSK-009 (list view), RSK-004 (RPN bands).
 * ============================================================================
 */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero, Button } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './RiskRegister.module.css';
import { NewRiskDialog } from './NewRiskDialog';
import { useRisksQuery, type RiskLevel, type RiskListItem, type RisksQueryParams } from './useRisksQuery';
import { AiClusterInsight } from '../../components/AiCluster';

type SortKey = 'rpn' | 'riskNumber' | 'riskLevel' | 'status' | 'nextReviewDueUtc';

const LEVEL_ORDER: Record<RiskLevel, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

const LEVEL_CLASS: Record<RiskLevel, string> = {
  CRITICAL: styles.levelCritical,
  HIGH: styles.levelHigh,
  MEDIUM: styles.levelMedium,
  LOW: styles.levelLow,
};

export interface RiskRegisterProps {
  onOpenRisk?: (riskId: string) => void;
}

export function RiskRegister({ onOpenRisk }: RiskRegisterProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<RisksQueryParams>({});
  const [sortKey, setSortKey] = useState<SortKey>('rpn');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showNew, setShowNew] = useState(false);

  const risksQ = useRisksQuery(filters);
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  const items: RiskListItem[] = risksQ.data?.items ?? [];

  const sortedItems = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'rpn':
          cmp = a.rpn - b.rpn;
          break;
        case 'riskNumber':
          cmp = a.riskNumber.localeCompare(b.riskNumber);
          break;
        case 'riskLevel':
          cmp = LEVEL_ORDER[a.riskLevel] - LEVEL_ORDER[b.riskLevel];
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
        case 'nextReviewDueUtc': {
          const av = a.nextReviewDueUtc ?? '';
          const bv = b.nextReviewDueUtc ?? '';
          cmp = av.localeCompare(bv);
          break;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  const isEmpty = !risksQ.isLoading && !risksQ.isError && items.length === 0;

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'rpn' ? 'desc' : 'asc');
    }
  }

  function handleOpen(r: RiskListItem) {
    if (onOpenRisk) {
      onOpenRisk(r.riskId);
      return;
    }
    navigate({ to: '/risks/$id', params: { id: r.riskId } });
  }

  // Hero KPI tiles
  const totals = useMemo(() => {
    const by: Record<RiskLevel, number> = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
    for (const r of items) by[r.riskLevel]++;
    return by;
  }, [items]);

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('risk.register.eyebrow', { defaultValue: 'Quality Events' })}
        title={t('risk.register.title', { defaultValue: 'Risk register' })}
        subtitle={t('risk.register.subtitle', {
          defaultValue:
            'Quality risk management per ICH Q9 — FMEA scoring, treatment plans, and review cadence.',
        })}
        actions={
          <Button variant="primary" onClick={() => setShowNew(true)}>
            {t('risk.register.newCta', { defaultValue: 'New risk' })}
          </Button>
        }
      />

      <section className={styles.statsRow} aria-label="Risk level totals">
        {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as RiskLevel[]).map((lvl) => (
          <div key={lvl} className={styles.statTile} data-level={lvl}>
            <div className={styles.statTileHeader}>
              <span className={styles.statTileLabel}>
                {t(`risk.level.${lvl}`, { defaultValue: lvl })}
              </span>
              <span className={`${styles.statDot} ${LEVEL_CLASS[lvl]}`} aria-hidden />
            </div>
            <div className={styles.statTileValue}>{nf.format(totals[lvl])}</div>
          </div>
        ))}
      </section>

      {/* AI-205 — Risk register clustering */}
      {items.length >= 2 && (
        <div style={{ margin: '12px 0' }}>
          <AiClusterInsight
            entityKind="risk"
            title={t('risk.register.aiClusters', { defaultValue: 'AI sees common-thread risks' })}
            minSimilarity={0.55}
            items={items.map((r) => ({
              id: r.riskId,
              text: `${r.title} (${r.riskCategory}, level ${r.riskLevel}, scope ${r.processScope})`,
              label: r.riskNumber,
              href: `/risks/${encodeURIComponent(r.riskId)}`,
            }))}
          />
        </div>
      )}

      <section className={styles.filterBar} aria-label="Filters">
        <label className={styles.filterField}>
          <span>{t('risk.filter.level', { defaultValue: 'Level' })}</span>
          <select
            value={filters.level ?? ''}
            onChange={(e) =>
              setFilters({ ...filters, level: (e.target.value || undefined) as RiskLevel | undefined })
            }
          >
            <option value="">{t('common.all', { defaultValue: 'All' })}</option>
            <option value="CRITICAL">CRITICAL</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
        </label>
        <label className={styles.filterField}>
          <span>{t('risk.filter.category', { defaultValue: 'Category' })}</span>
          <select
            value={filters.category ?? ''}
            onChange={(e) => setFilters({ ...filters, category: (e.target.value || undefined) as never })}
          >
            <option value="">{t('common.all', { defaultValue: 'All' })}</option>
            <option value="PRODUCT_QUALITY">Product quality</option>
            <option value="PATIENT_SAFETY">Patient safety</option>
            <option value="DATA_INTEGRITY">Data integrity</option>
            <option value="REGULATORY">Regulatory</option>
            <option value="SUPPLY">Supply</option>
            <option value="EQUIPMENT">Equipment</option>
            <option value="CYBER">Cyber</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className={styles.filterField}>
          <span>{t('risk.filter.status', { defaultValue: 'Status' })}</span>
          <select
            value={filters.status ?? ''}
            onChange={(e) => setFilters({ ...filters, status: (e.target.value || undefined) as never })}
          >
            <option value="">{t('common.all', { defaultValue: 'All' })}</option>
            <option value="OPEN">OPEN</option>
            <option value="MITIGATING">MITIGATING</option>
            <option value="MONITORED">MONITORED</option>
            <option value="CLOSED">CLOSED</option>
            <option value="ACCEPTED">ACCEPTED</option>
          </select>
        </label>
      </section>

      {risksQ.isLoading && (
        <div className={styles.skeleton}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.skeletonRow} />
          ))}
        </div>
      )}

      {risksQ.isError && (
        <div className={styles.errorEnvelope} role="alert">
          <p>{t('risk.register.error', { defaultValue: 'Failed to load risks.' })}</p>
          <button type="button" onClick={() => risksQ.refetch()} className={styles.retryBtn}>
            {t('common.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      )}

      {isEmpty && (
        <div className={styles.emptyState}>
          <p>
            {t('risk.register.empty', {
              defaultValue:
                'No risks registered yet. Register a risk to begin the FMEA cycle (ICH Q9).',
            })}
          </p>
        </div>
      )}

      {!risksQ.isLoading && !isEmpty && (
        <table className={styles.table} aria-label="Risk register table">
          <thead>
            <tr>
              <th>
                <button type="button" className={styles.sortBtn} onClick={() => toggleSort('riskNumber')}>
                  # {sortKey === 'riskNumber' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>{t('risk.col.title', { defaultValue: 'Title' })}</th>
              <th>{t('risk.col.category', { defaultValue: 'Category' })}</th>
              <th className={styles.numCol}>S</th>
              <th className={styles.numCol}>O</th>
              <th className={styles.numCol}>D</th>
              <th className={styles.numCol}>
                <button type="button" className={styles.sortBtn} onClick={() => toggleSort('rpn')}>
                  RPN {sortKey === 'rpn' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>
                <button type="button" className={styles.sortBtn} onClick={() => toggleSort('riskLevel')}>
                  {t('risk.col.level', { defaultValue: 'Level' })}{' '}
                  {sortKey === 'riskLevel' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>
                <button type="button" className={styles.sortBtn} onClick={() => toggleSort('status')}>
                  {t('risk.col.status', { defaultValue: 'Status' })}
                </button>
              </th>
              <th>
                <button type="button" className={styles.sortBtn} onClick={() => toggleSort('nextReviewDueUtc')}>
                  {t('risk.col.nextReview', { defaultValue: 'Next review' })}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((r) => (
              <tr
                key={r.riskId}
                tabIndex={0}
                className={styles.dataRow}
                onClick={() => handleOpen(r)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleOpen(r);
                }}
              >
                <td className={styles.mono}>{r.riskNumber}</td>
                <td>{r.title}</td>
                <td>{r.riskCategory}</td>
                <td className={styles.numCol}>{r.severityScore}</td>
                <td className={styles.numCol}>{r.occurrenceScore}</td>
                <td className={styles.numCol}>{r.detectabilityScore}</td>
                <td className={`${styles.numCol} ${styles.rpnCell}`}>{nf.format(r.rpn)}</td>
                <td>
                  <span className={`${styles.levelChip} ${LEVEL_CLASS[r.riskLevel]}`}>
                    <span className={styles.dot} aria-hidden />
                    {r.riskLevel}
                  </span>
                </td>
                <td>{r.status}</td>
                <td>
                  {r.nextReviewDueUtc
                    ? dateFmt.format(new Date(r.nextReviewDueUtc))
                    : t('common.dash', { defaultValue: '—' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <NewRiskDialog
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            void risksQ.refetch();
          }}
        />
      )}
    </div>
  );
}
